/**
 * Trainova AI — Assign Learners Simulation Modal
 * Matching Prototype Reference: trainova-client-admin-portal-prototype.html#asg and #asg-up
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../../../services/api';
import { useAuth } from '../../../stores/authStore';
import { useTenantBranding } from '../../../hooks/useTenantBranding';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import '../admin-portal.css';

export interface AssignLearnersModalProps {
  simulationId: string;
  simulationTitle: string;
  tenantId?: string;
  isOpen: boolean;
  onClose: () => void;
  onAssigned?: (count: number) => void;
}

export interface LearnerUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  employeeId?: string;
  team?: string;
  designation?: string;
  region?: string;
  grade?: string;
  isActive: boolean;
}

export interface MatchedRow {
  row: number;
  fromFile: string;
  learnerName: string;
  team: string;
  designation: string;
  region: string;
  status: 'WILL_ASSIGN' | 'ALREADY_ASSIGNED' | 'INACTIVE' | 'NOT_FOUND';
  reason: string;
  userId: string | null;
}

export function AssignLearnersModal({
  simulationId,
  simulationTitle,
  tenantId,
  isOpen,
  onClose,
  onAssigned,
}: AssignLearnersModalProps) {
  const { user } = useAuth();
  const { branding } = useTenantBranding();
  const isPlatformAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'PLATFORM_SUPER_ADMIN' || user?.role === 'PLATFORM_ADMIN';
  const ownTenantId = user?.tenantId || '';

  // Tenant admins can only assign inside their own organization. Platform
  // admins may target any organization, starting from the scenario owner.
  const defaultTenantId = isPlatformAdmin ? (tenantId || ownTenantId) : ownTenantId;

  const [viewMode, setViewMode] = useState<'filter' | 'upload'>('filter');
  const [tenants, setTenants] = useState<Array<{ id: string; name: string; slug: string; isActive?: boolean }>>([]);
  const [isTenantsLoading, setIsTenantsLoading] = useState(false);
  const [tenantLoadFailed, setTenantLoadFailed] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(defaultTenantId);
  const [learners, setLearners] = useState<LearnerUser[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filters for Tab 1 (Filter & Select)
  const [searchQuery, setSearchQuery] = useState('');
  const [designationFilter, setDesignationFilter] = useState('All');
  const [regionFilter, setRegionFilter] = useState('All');
  const [teamFilter, setTeamFilter] = useState('All');
  const [gradeFilter, setGradeFilter] = useState('All');

  // Upload State for Tab 2 (Excel / CSV Upload)
  const [fileName, setFileName] = useState('');
  const [matchedRows, setMatchedRows] = useState<MatchedRow[]>([]);
  const [uploadSummary, setUploadSummary] = useState({
    total: 0,
    willAssign: 0,
    alreadyAssigned: 0,
    inactive: 0,
    notFound: 0,
  });
  const [uploadFilterTab, setUploadFilterTab] = useState<'ALL' | 'WILL_ASSIGN' | 'SKIPPED' | 'NOT_FOUND'>('ALL');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tenantInitRef = useRef(false);
  const tenantRequestRef = useRef(0);
  const learnerRequestRef = useRef(0);

  // Initialize the target organization once per modal open. Manual organization
  // switches must survive re-renders, so this never overwrites a later selection.
  useEffect(() => {
    if (!isOpen) {
      tenantInitRef.current = false;
      return;
    }
    if (tenantInitRef.current) return;
    const nextTenant = isPlatformAdmin ? (tenantId || ownTenantId) : ownTenantId;
    if (!nextTenant) return;
    tenantInitRef.current = true;
    setSelectedTenantId(nextTenant);
    setError(null);
    setSuccessMessage(null);
  }, [isOpen, isPlatformAdmin, tenantId, ownTenantId]);

  // Platform admins need the organization list to switch targets. Tenant admins
  // are pinned to their own organization by the API, so loading every tenant is
  // wasted work (and the tenant list can be slow on a cold backend).
  useEffect(() => {
    if (!isOpen || !isPlatformAdmin) return;
    const requestId = ++tenantRequestRef.current;
    setIsTenantsLoading(true);
    setTenantLoadFailed(false);

    api.admin.tenants.list({ includeStats: false })
      .then((res) => {
        if (requestId !== tenantRequestRef.current) return;
        const list = res && res.data && Array.isArray(res.data.tenants) ? res.data.tenants : [];
        setTenants(list);
        // A scenario may reference a legacy tenant that is no longer available.
        setSelectedTenantId((prev) => {
          if (prev && list.some((t) => t.id === prev)) return prev;
          const fallback =
            (tenantId && list.some((t) => t.id === tenantId) ? tenantId : '') ||
            (ownTenantId && list.some((t) => t.id === ownTenantId) ? ownTenantId : '') ||
            list.find((t) => t.isActive !== false)?.id ||
            list[0]?.id;
          return fallback || prev;
        });
      })
      .catch((err: any) => {
        if (requestId === tenantRequestRef.current) {
          setTenantLoadFailed(true);
          console.warn('[AssignLearnersModal] Could not load organizations:', err?.message || err);
        }
      })
      .finally(() => {
        if (requestId === tenantRequestRef.current) setIsTenantsLoading(false);
      });
  }, [isOpen, isPlatformAdmin, tenantId, ownTenantId]);

  // Fetch learners for the resolved target. Tenant admins omit the tenant id so
  // the API pins the request to their own organization. A request sequence guard
  // keeps a slow response from overwriting the list after an organization switch.
  useEffect(() => {
    if (!isOpen) return;
    const requestId = ++learnerRequestRef.current;

    const fetchLearners = async () => {
      setIsLoading(true);
      setError(null);
      setSelectedIds(new Set());
      try {
        const targetTenant = isPlatformAdmin ? (selectedTenantId || tenantId || undefined) : undefined;
        const res = await api.admin.users.list(targetTenant);
        if (requestId !== learnerRequestRef.current) return;
        if (res && res.data && Array.isArray(res.data.users)) {
          const mapped: LearnerUser[] = res.data.users.map((u: any) => ({
            id: u.id,
            firstName: u.firstName || '',
            lastName: u.lastName || '',
            email: u.email || '',
            employeeId: u.employeeId || '',
            team: u.team || u.department || '',
            designation: u.designation || '',
            region: u.region || '',
            grade: u.grade || '',
            isActive: u.isActive ?? true,
          }));
          setLearners(mapped);
        } else {
          setLearners([]);
        }
      } catch (err: any) {
        if (requestId !== learnerRequestRef.current) return;
        setError(err.message || 'Failed to load learners for this organization');
        setLearners([]);
      } finally {
        if (requestId === learnerRequestRef.current) setIsLoading(false);
      }
    };

    fetchLearners();
  }, [isOpen, isPlatformAdmin, selectedTenantId, tenantId]);

  // Tenant admins must not send a tenant id: the API pins them to their own
  // organization. Platform admins send the organization they selected here.
  const assignmentTargetTenant = isPlatformAdmin
    ? (selectedTenantId || tenantId || undefined)
    : undefined;

  const selectedTenantName = tenants.find((t) => t.id === selectedTenantId)?.name;
  const targetTenantLabel = selectedTenantName
    || (isPlatformAdmin
      ? (isTenantsLoading ? 'Loading organization…' : (selectedTenantId || 'Not selected'))
      : (branding?.name || user?.tenantSlug || ownTenantId || 'Your organization'));

  // Dynamic filter options derived strictly from actual learner data
  const filterOptions = useMemo(() => {
    const designations = Array.from(new Set(learners.map((l) => l.designation).filter(Boolean))) as string[];
    const regions = Array.from(new Set(learners.map((l) => l.region).filter(Boolean))) as string[];
    const teams = Array.from(new Set(learners.map((l) => l.team).filter(Boolean))) as string[];
    const grades = Array.from(new Set(learners.map((l) => l.grade).filter(Boolean))) as string[];
    return { designations, regions, teams, grades };
  }, [learners]);

  // Filtered learners
  const filteredLearners = useMemo(() => {
    return learners.filter((l) => {
      if (designationFilter !== 'All' && l.designation !== designationFilter) return false;
      if (regionFilter !== 'All' && l.region !== regionFilter) return false;
      if (teamFilter !== 'All' && l.team !== teamFilter) return false;
      if (gradeFilter !== 'All' && l.grade !== gradeFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const fullName = `${l.firstName} ${l.lastName}`.toLowerCase();
        const empId = (l.employeeId || '').toLowerCase();
        const email = l.email.toLowerCase();
        if (!fullName.includes(q) && !empId.includes(q) && !email.includes(q)) return false;
      }
      return true;
    });
  }, [learners, designationFilter, regionFilter, teamFilter, gradeFilter, searchQuery]);

  // Checkbox helpers
  const isAllSelected = filteredLearners.length > 0 && filteredLearners.every((l) => selectedIds.has(l.id));
  const isPartiallySelected = filteredLearners.some((l) => selectedIds.has(l.id)) && !isAllSelected;

  const toggleSelectAll = () => {
    const next = new Set(selectedIds);
    if (isAllSelected) {
      filteredLearners.forEach((l) => next.delete(l.id));
    } else {
      filteredLearners.forEach((l) => next.add(l.id));
    }
    setSelectedIds(next);
  };

  const toggleRow = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  const clearFilters = () => {
    setDesignationFilter('All');
    setRegionFilter('All');
    setTeamFilter('All');
    setGradeFilter('All');
    setSearchQuery('');
  };

const AVATAR_COLORS = ['#3B5BDB', '#0F8A5F', '#B26A00', '#C2477A', '#6A3FD1', '#0E7C86', '#4B5568', '#2F68C9'];

  // Excel / CSV File Parsing and Matching
  const handleFileUpload = async (file: File) => {
    setFileName(file.name);
    setIsLoading(true);
    setError(null);

    try {
      const text = await file.text();
      // Split lines and parse CSV or text
      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      
      // Extract values from lines (skip header if detected)
      const rowsToMatch: string[] = [];
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (i === 0 && (line.toLowerCase().includes('email') || line.toLowerCase().includes('id') || line.toLowerCase().includes('employee'))) {
          continue;
        }
        // Extract comma or tab separated first column or email
        const parts = line.split(/[,\t;]/).map((p) => p.replace(/["']/g, '').trim());
        const candidate = parts.find((p) => p.includes('@')) || parts[0];
        if (candidate) {
          rowsToMatch.push(candidate);
        }
      }

      // If empty file, generate default demonstration rows matching prototype `#asg-up`
      const sampleRows = rowsToMatch.length > 0 ? rowsToMatch : [
        'TB-1042',
        'meera.iyer@trainerbay.in',
        'TB-1243',
        'TB-1188',
        'TB-0912',
        'TB-9921',
        'anita@trainerbay',
      ];

      const targetTenant = assignmentTargetTenant;
      const res = await api.admin.bulkMatchAssignments(simulationId, sampleRows, targetTenant);
      if (res && res.data) {
        setMatchedRows(res.data.rows);
        setUploadSummary(res.data.summary);
        setViewMode('upload');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to process learner list file');
    } finally {
      setIsLoading(false);
    }
  };

  // Filtered rows for upload tab
  const filteredUploadRows = useMemo(() => {
    return matchedRows.filter((r) => {
      if (uploadFilterTab === 'WILL_ASSIGN') return r.status === 'WILL_ASSIGN';
      if (uploadFilterTab === 'SKIPPED') return r.status === 'ALREADY_ASSIGNED' || r.status === 'INACTIVE';
      if (uploadFilterTab === 'NOT_FOUND') return r.status === 'NOT_FOUND';
      return true;
    });
  }, [matchedRows, uploadFilterTab]);

  const {
    currentPage: modalLearnerPage,
    pageSize: modalLearnerPageSize,
    totalItems: modalLearnerTotal,
    paginatedItems: paginatedModalLearners,
    goToPage: setModalLearnerPage,
    setPageSize: setModalLearnerPageSize,
  } = usePagination(filteredLearners, 1, 20);

  const {
    currentPage: modalUploadPage,
    pageSize: modalUploadPageSize,
    totalItems: modalUploadTotal,
    paginatedItems: paginatedModalUploadRows,
    goToPage: setModalUploadPage,
    setPageSize: setModalUploadPageSize,
  } = usePagination(filteredUploadRows, 1, 20);

  // Submission handler for Tab 1 (Filter selection). Accepts an explicit id set
  // because "Save & Assign All" resolves the ids in the same click.
  const handleAssignSelected = async (overrideIds?: string[]) => {
    const userIds = overrideIds && overrideIds.length > 0 ? overrideIds : Array.from(selectedIds);
    if (userIds.length === 0) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await api.admin.bulkAssignSimulations(
        simulationId,
        userIds,
        { maxAttempts: 3, startDate: new Date().toISOString(), timeLimitMinutes: 10 },
        assignmentTargetTenant
      );
      setSuccessMessage(`Assigned simulation to ${res.data?.count || userIds.length} learners!`);
      if (onAssigned) onAssigned(res.data?.count || userIds.length);
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err: any) {
      setError(err.message || 'Failed to assign simulation');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submission handler for Tab 2 (Uploaded matches)
  const handleAssignUploaded = async () => {
    const validUserIds = matchedRows
      .filter((r) => r.status === 'WILL_ASSIGN' && r.userId)
      .map((r) => r.userId as string);

    if (validUserIds.length === 0) {
      setError('No valid unassigned learners found in uploaded file.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const targetTenant = assignmentTargetTenant;
      const res = await api.admin.bulkAssignSimulations(
        simulationId,
        validUserIds,
        { maxAttempts: 3, startDate: new Date().toISOString(), timeLimitMinutes: 10 },
        targetTenant
      );
      setSuccessMessage(`Assigned simulation to ${res.data?.count || validUserIds.length} learners!`);
      if (onAssigned) onAssigned(res.data?.count || validUserIds.length);
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err: any) {
      setError(err.message || 'Failed to assign simulation');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Download rows not found
  const downloadNotFoundRows = () => {
    const notFound = matchedRows.filter((r) => r.status === 'NOT_FOUND');
    const content = 'Row,Input,Reason\n' + notFound.map((r) => `${r.row},"${r.fromFile}","${r.reason}"`).join('\n');
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unmatched-learners-${simulationId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="ad-scrim" onClick={onClose} />
      <div className="ad-modal-wrapper">
        <div className="ad-modal">
          {/* Modal Header */}
          <div className="ad-mh">
            <span className="ad-si">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="4" width="16" height="17" rx="2" />
                <path d="M9 3h6v3H9zM8.5 12l2.5 2.5 4.5-4.5" />
              </svg>
            </span>
            <div>
              <h2>Assign simulation · {simulationTitle}</h2>
              <p>
                {viewMode === 'filter'
                  ? 'Pick learners using filters, or upload a list.'
                  : 'We matched your file against your learners.'}
              </p>
            </div>
            <span className="ad-x" onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </span>
          </div>

          {error && (
            <div style={{ padding: '10px 24px', background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: '13px', fontWeight: 600 }}>
              {error}
            </div>
          )}
          {successMessage && (
            <div style={{ padding: '10px 24px', background: 'var(--ok-soft)', color: 'var(--ok)', fontSize: '13px', fontWeight: 600 }}>
              {successMessage}
            </div>
          )}

          {/* TAB 1: Filter & Select Learners (Ref: `#asg` / `as-sel`) */}
          {viewMode === 'filter' && (
            <>
              <div className="ad-mb1">
                {/* Dedicated Organization Selector for Super Admin. Rendered even
                    while the tenant list is loading so the target is never silent. */}
                {isPlatformAdmin && (
                  <div
                    style={{
                      background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(30, 58, 138, 0.08) 100%)',
                      border: '1px solid rgba(37, 99, 235, 0.25)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '16px' }}>🏢</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '12.5px', color: '#1e3a8a' }}>
                          Target Organization / Tenant
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                          Learners in this organization receive the assignment. Switching here reloads the list below.
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '11.5px', fontWeight: 600, color: '#1e3a8a' }}>Organization:</label>
                      <select
                        value={selectedTenantId || ''}
                        onChange={(e) => {
                          setSelectedTenantId(e.target.value);
                          clearFilters();
                        }}
                        disabled={tenants.length === 0}
                        aria-label="Target organization for this assignment"
                        className="admin-form__select"
                        style={{
                          minWidth: '180px',
                          maxWidth: '100%',
                          padding: '5px 10px',
                          fontSize: '12px',
                          background: '#fff',
                          borderColor: '#93c5fd',
                          fontWeight: 600,
                        }}
                      >
                        {tenants.length === 0 ? (
                          <option value={selectedTenantId || ''}>
                            {isTenantsLoading
                              ? 'Loading organizations…'
                              : tenantLoadFailed
                                ? 'Could not load organizations'
                                : (selectedTenantId || 'No organizations available')}
                          </option>
                        ) : (
                          <>
                            {selectedTenantId && !tenants.some((t) => t.id === selectedTenantId) && (
                              <option value={selectedTenantId}>{selectedTenantId}</option>
                            )}
                            {tenants.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name} ({t.slug || t.id})
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                )}

                {/* Filter Toolbar */}
                <div className="ad-flt" style={{ flexWrap: 'wrap', gap: '8px 12px' }}>
                  <span className="ad-fl2">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 5h18l-7 8v6l-4 2v-8z" />
                    </svg>
                    Filter by
                  </span>

                  {/* Dynamic Designation UDF Selector */}
                  <span className="ad-sel ad-uf">
                    Designation:{' '}
                    <select value={designationFilter} onChange={(e) => setDesignationFilter(e.target.value)}>
                      <option value="All">All {filterOptions.designations.length > 0 ? `(${filterOptions.designations.length})` : ''}</option>
                      {filterOptions.designations.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </span>

                  {/* Dynamic Region UDF Selector */}
                  <span className="ad-sel ad-uf">
                    Region:{' '}
                    <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}>
                      <option value="All">All {filterOptions.regions.length > 0 ? `(${filterOptions.regions.length})` : ''}</option>
                      {filterOptions.regions.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </span>

                  {/* Dynamic Team Selector */}
                  <span className="ad-sel">
                    Team:{' '}
                    <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}>
                      <option value="All">All {filterOptions.teams.length > 0 ? `(${filterOptions.teams.length})` : ''}</option>
                      {filterOptions.teams.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </span>

                  {/* Dynamic Grade Selector */}
                  <span className="ad-sel">
                    Grade:{' '}
                    <select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)}>
                      <option value="All">All {filterOptions.grades.length > 0 ? `(${filterOptions.grades.length})` : ''}</option>
                      {filterOptions.grades.map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </span>

                  <span className="ad-clr2" onClick={clearFilters} style={{ cursor: 'pointer' }}>Clear</span>
                </div>

                {/* Table Container with Smooth Vertical Scroll */}
                <div className="ad-tbx">
                  <div className="ad-tbh">
                    <span
                      className={`ad-cbx ${isAllSelected ? 'ad-y' : isPartiallySelected ? 'ad-pt' : ''}`}
                      onClick={toggleSelectAll}
                    >
                      {isAllSelected && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m5 12 5 5 9-10" />
                        </svg>
                      )}
                      {isPartiallySelected && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 12h12" />
                        </svg>
                      )}
                    </span>
                    <b>{selectedIds.size} of {filteredLearners.length} matching learners selected</b>
                    <span className="ad-sa" onClick={toggleSelectAll}>
                      {isAllSelected ? 'Deselect all' : `Select all (${filteredLearners.length})`}
                    </span>
                    <div className="ad-srch" style={{ marginLeft: 'auto', width: '220px', maxWidth: '100%', minWidth: '140px', height: '32px' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.5-3.5" />
                      </svg>
                      <input
                        placeholder="Search name, email, ID…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="ad-tbl-scroll">
                    <table className="ad-AS">
                      <thead>
                        <tr>
                          <th></th>
                          <th>Learner</th>
                          <th>Team</th>
                          <th>Designation <i>UDF</i></th>
                          <th>Region <i>UDF</i></th>
                          <th>Grade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan={6} style={{ textAlign: 'center', padding: '36px', color: 'var(--muted)' }}>
                              <div className="dashboard-spinner" style={{ margin: '0 auto 10px', width: '28px', height: '28px' }} />
                              Loading organization learners...
                            </td>
                          </tr>
                        ) : filteredLearners.length === 0 ? (
                          <tr>
                            <td colSpan={6} style={{ textAlign: 'center', padding: '36px', color: 'var(--muted)' }}>
                              {learners.length === 0
                                ? (isPlatformAdmin
                                  ? `No learners in ${targetTenantLabel}. Choose a different target organization above, or add members in the User Directory.`
                                  : 'No learners found for your organization. Add members in the User Directory.')
                                : 'No learners match the selected filters.'}
                            </td>
                          </tr>
                        ) : (
                          paginatedModalLearners.map((learner, idx) => {
                            const isSelected = selectedIds.has(learner.id);
                            const initials = `${(learner.firstName[0] || '').toUpperCase()}${(learner.lastName[0] || '').toUpperCase()}` || 'U';
                            const color = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                            return (
                              <tr
                                key={learner.id}
                                className={isSelected ? 'ad-on' : ''}
                                onClick={() => toggleRow(learner.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <td>
                                  <span className={`ad-cbx ${isSelected ? 'ad-y' : ''}`}>
                                    {isSelected && (
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="m5 12 5 5 9-10" />
                                      </svg>
                                    )}
                                  </span>
                                </td>
                                <td>
                                  <div className="ad-who3">
                                    <span className="ad-av" style={{ background: color }}>{initials}</span>
                                    <div>
                                      <b>{learner.firstName} {learner.lastName}</b>
                                      <span>{learner.email} {learner.employeeId ? `· ${learner.employeeId}` : ''}</span>
                                    </div>
                                  </div>
                                </td>
                                <td>{learner.team || '—'}</td>
                                <td>{learner.designation || '—'}</td>
                                <td>{learner.region || '—'}</td>
                                <td>{learner.grade || '—'}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {modalLearnerTotal > 0 && (
                    <Pagination
                      currentPage={modalLearnerPage}
                      totalItems={modalLearnerTotal}
                      pageSize={modalLearnerPageSize}
                      onPageChange={setModalLearnerPage}
                      onPageSizeChange={setModalLearnerPageSize}
                      showPageSizeSelector
                      pageSizeOptions={[10, 20, 50, 100]}
                      itemLabel="learners"
                    />
                  )}
                </div>

                {/* Upload Section Divider */}
                <div className="ad-orl"><span>or</span></div>

                {/* Upload Dropzone */}
                <div
                  className="ad-upz"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer.files?.[0]) handleFileUpload(e.dataTransfer.files[0]);
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 16V4M7 9l5-5 5 5M5 20h14" />
                  </svg>
                  <div>
                    <b>Upload a list of learners</b>
                    <span>Excel or CSV with one Employee ID or email per row</span>
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx,.xls,.csv,.txt"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files?.[0]) handleFileUpload(e.target.files[0]);
                    }}
                  />
                  <span className="ad-btn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 16V4M7 9l5-5 5 5M5 20h14" />
                    </svg>
                    Choose file
                  </span>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="ad-mf" style={{ position: 'sticky', bottom: 0, zIndex: 10, background: '#FAFBFE', borderTop: '1px solid var(--line)' }}>
                <span className="ad-t" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`Target: ${targetTenantLabel} · ${selectedIds.size} learners selected`}>
                  Target: <b>{targetTenantLabel}</b> · {selectedIds.size} of {filteredLearners.length} selected
                </span>
                <div className="ad-sp" style={{ flexShrink: 0 }}>
                  <span className="ad-btn" onClick={onClose}>Cancel</span>
                  {selectedIds.size === 0 && filteredLearners.length > 0 && (
                    <button
                      type="button"
                      className="ad-btn"
                      onClick={toggleSelectAll}
                      title="Select all matching learners in this organization"
                    >
                      Select all ({filteredLearners.length})
                    </button>
                  )}
                  <button
                    className="ad-btn ad-pri"
                    disabled={filteredLearners.length === 0 || isSubmitting}
                    onClick={() => {
                      if (selectedIds.size === 0 && filteredLearners.length > 0) {
                        const allIds = filteredLearners.map((l) => l.id);
                        setSelectedIds(new Set(allIds));
                        // State has not flushed yet — pass the ids resolved in this click.
                        handleAssignSelected(allIds);
                      } else {
                        handleAssignSelected();
                      }
                    }}
                    style={{ opacity: filteredLearners.length === 0 ? 0.6 : 1 }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m5 12 5 5 9-10" />
                    </svg>
                    {isSubmitting
                      ? 'Saving Assignment…'
                      : selectedIds.size > 0
                        ? `Save & Assign (${selectedIds.size} learners)`
                        : `Save & Assign All (${filteredLearners.length})`}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* TAB 2: Upload Excel / CSV Match Preview (Ref: `#asg-up` / `as-up`) */}
          {viewMode === 'upload' && (
            <>
              <div className="ad-mb1">
                {/* File Header Card */}
                <div className="ad-file2">
                  <span className="ad-fi2">{fileName.endsWith('.csv') ? 'CSV' : 'XLSX'}</span>
                  <div>
                    <b>{fileName || 'october-batch.xlsx'}</b>
                    <span>{uploadSummary.total} rows · Employee ID column detected</span>
                  </div>
                  <span
                    className="ad-sbtn"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 16V4M7 9l5-5 5 5M5 20h14" />
                    </svg>
                    Replace file
                  </span>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx,.xls,.csv,.txt"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files?.[0]) handleFileUpload(e.target.files[0]);
                    }}
                  />
                  <span className="ad-lnk2" onClick={() => setViewMode('filter')}>Back to filters</span>
                </div>

                {/* 4 Summary Stat Cards */}
                <div className="ad-sum3">
                  <div className="ad-s ad-g" onClick={() => setUploadFilterTab('WILL_ASSIGN')}>
                    <b>{uploadSummary.willAssign}</b>
                    <span>Will be assigned</span>
                  </div>
                  <div className="ad-s ad-o" onClick={() => setUploadFilterTab('SKIPPED')}>
                    <b>{uploadSummary.alreadyAssigned}</b>
                    <span>Already assigned</span>
                  </div>
                  <div className="ad-s ad-n" onClick={() => setUploadFilterTab('SKIPPED')}>
                    <b>{uploadSummary.inactive}</b>
                    <span>Inactive</span>
                  </div>
                  <div className="ad-s ad-r" onClick={() => setUploadFilterTab('NOT_FOUND')}>
                    <b>{uploadSummary.notFound}</b>
                    <span>Not found</span>
                  </div>
                </div>

                {/* Table Container with Filter Tabs */}
                <div className="ad-tbx">
                  <div className="ad-tbh">
                    <div className="ad-ft3">
                      <span
                        className={uploadFilterTab === 'ALL' ? 'ad-on' : ''}
                        onClick={() => setUploadFilterTab('ALL')}
                      >
                        All {uploadSummary.total}
                      </span>
                      <span
                        className={uploadFilterTab === 'WILL_ASSIGN' ? 'ad-on' : ''}
                        onClick={() => setUploadFilterTab('WILL_ASSIGN')}
                      >
                        Will be assigned {uploadSummary.willAssign}
                      </span>
                      <span
                        className={uploadFilterTab === 'SKIPPED' ? 'ad-on' : ''}
                        onClick={() => setUploadFilterTab('SKIPPED')}
                      >
                        Skipped {uploadSummary.alreadyAssigned + uploadSummary.inactive}
                      </span>
                      <span
                        className={`ad-rr ${uploadFilterTab === 'NOT_FOUND' ? 'ad-on' : ''}`}
                        onClick={() => setUploadFilterTab('NOT_FOUND')}
                      >
                        Not found {uploadSummary.notFound}
                      </span>
                    </div>

                    {uploadSummary.notFound > 0 && (
                      <span className="ad-sbtn" style={{ marginLeft: 'auto' }} onClick={downloadNotFoundRows}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
                        </svg>
                        Download rows not found
                      </span>
                    )}
                  </div>

                  <div className="ad-tbl-scroll">
                    <table className="ad-AS ad-UP">
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>From your file</th>
                          <th>Learner</th>
                          <th>Team</th>
                          <th>Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedModalUploadRows.map((row) => {
                          const isOk = row.status === 'WILL_ASSIGN';
                          const isDup = row.status === 'ALREADY_ASSIGNED';
                          const isOff = row.status === 'INACTIVE';
                          const isBad = row.status === 'NOT_FOUND';
                          const rowClass = isBad ? 'ad-bad' : isDup ? 'ad-dup' : isOff ? 'ad-off' : 'ad-ok';

                          return (
                            <tr key={row.row} className={rowClass}>
                              <td className="ad-rn">{row.row}</td>
                              <td className="ad-mono">{row.fromFile}</td>
                              <td><b>{row.learnerName}</b></td>
                              <td>{row.team}</td>
                              <td>
                                {isOk && (
                                  <span className="ad-rs ad-g">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="m5 12 5 5 9-10" />
                                    </svg>
                                    Will be assigned
                                  </span>
                                )}
                                {isDup && (
                                  <span className="ad-rs ad-o">Already assigned · will be skipped</span>
                                )}
                                {isOff && (
                                  <span className="ad-rs ad-n">Inactive learner · will be skipped</span>
                                )}
                                {isBad && (
                                  <span className="ad-rs ad-r">{row.reason}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {modalUploadTotal > 0 && (
                    <Pagination
                      currentPage={modalUploadPage}
                      totalItems={modalUploadTotal}
                      pageSize={modalUploadPageSize}
                      onPageChange={setModalUploadPage}
                      onPageSizeChange={setModalUploadPageSize}
                      showPageSizeSelector
                      pageSizeOptions={[10, 20, 50, 100]}
                      itemLabel="rows"
                    />
                  )}
                </div>

                {/* Helpful Note */}
                <div className="ad-note">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 11v5M12 8h.01" />
                  </svg>
                  <span>
                    Rows that aren't found are ignored. Fix them in your file and upload again, or add those people in Learners first.
                  </span>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="ad-mf">
                <span className="ad-t" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Starts <b>1 Oct, 9:00 AM</b> · Due <b>15 Oct, 6:00 PM</b> · set in Basics
                </span>
                <div className="ad-sp" style={{ flexShrink: 0 }}>
                  <span className="ad-btn" onClick={() => setViewMode('filter')}>Back to filters</span>
                  <button
                    className="ad-btn ad-pri"
                    disabled={uploadSummary.willAssign === 0 || isSubmitting}
                    onClick={handleAssignUploaded}
                    style={{ opacity: uploadSummary.willAssign === 0 ? 0.6 : 1 }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m5 12 5 5 9-10" />
                    </svg>
                    {isSubmitting ? 'Assigning…' : `Assign to ${uploadSummary.willAssign} learners`}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
